import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiGet } from '../../../../api/queries/apiGet';
import { API_BASE_URL } from '../constants/consts';
import { authFirewallQueryKeys } from '../constants/queryKeys';
import { EntityDetails, EntityDetailsFull } from '../../types';
import { buildUniversalMfaDomainQueryParams } from '../constants/utils';
import { ActiveUsersAndResourcesMode, QueryFiltersDomainOnly } from '../types';

const END_POINT = '/metric/activeResources';
const PREVIEW_MODE_LIMIT = 9;
const FULL_MODE_LIMIT = 50;

type ActiveResourcesDto = EntityDetails;
type ActiveResourcesFullDto = Omit<EntityDetailsFull, 'lastAuthTime'> & { lastAuthTime: string };

const convertToActiveResources = (data: ActiveResourcesDto[]): EntityDetails[] => {
    return data.map((item) => ({
        name: item.name,
        hits: item.hits,
        guid: item.guid
    }));
};

const convertToActiveResourcesFull = (data: ActiveResourcesFullDto[]): EntityDetailsFull[] => {
    return data.map((item) => ({
        name: item.name,
        hits: item.hits,
        guid: item.guid,
        lastAuthTime: new Date(item.lastAuthTime),
        riskLevel: item.riskLevel,
        domain: item.domain
    }));
};

const fetchActiveResources = async (
    filters: QueryFiltersDomainOnly,
    mode: ActiveUsersAndResourcesMode
): Promise<ActiveResourcesDto[] | ActiveResourcesFullDto[]> => {
    const data = await apiGet<ActiveResourcesDto[] | ActiveResourcesFullDto[]>(`${API_BASE_URL}${END_POINT}`, {
        ...buildUniversalMfaDomainQueryParams(filters),
        limit: mode === 'preview' ? PREVIEW_MODE_LIMIT.toString() : FULL_MODE_LIMIT.toString(),
        mode
    });
    return data;
};

export const useActiveResourcesPreview = (filters: QueryFiltersDomainOnly): UseQueryResult<EntityDetails[]> => {
    return useQuery({
        queryKey: authFirewallQueryKeys.activeResources(filters, 'preview'),
        queryFn: () => fetchActiveResources(filters, 'preview') as Promise<ActiveResourcesDto[]>,
        select: convertToActiveResources
    });
};

export const useActiveResourcesFull = (
    filters: QueryFiltersDomainOnly,
    enabled: boolean
): UseQueryResult<EntityDetailsFull[]> => {
    return useQuery({
        queryKey: authFirewallQueryKeys.activeResources(filters, 'full'),
        queryFn: () => fetchActiveResources(filters, 'full') as Promise<ActiveResourcesFullDto[]>,
        select: convertToActiveResourcesFull,
        enabled
    });
};
